import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/api";
import { z } from "zod";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import {
  Edit,
  Trash2,
  PlusCircle,
  RefreshCw,
  Info,
  AlertTriangle,
  Bell,
  BadgeAlert,
  Megaphone,
  ArrowUpCircle,
  CheckCircle2,
  XCircle,
  EyeOff,
  Eye,
  Power,
  PowerOff,
  CalendarIcon,
  UsersIcon,
  SettingsIcon,
  LayoutIcon,
  FileTextIcon
} from "lucide-react";
import { ImageUploadField } from "@/components/ui/image-upload-field";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";

// Types for announcements
type AnnouncementType = 'info' | 'warning' | 'critical' | 'promo' | 'update';
type TargetAudience = 'all' | 'free' | 'premium' | 'vip';

// Interface to handle different image upload response formats
interface ImageUploadResponse {
  imageUrl?: string;
  url?: string;
  image?: string;
  path?: string;
  [key: string]: any; // To handle any other property names
}

interface Announcement {
  id: string;
  title: string;
  content: string;
  type: AnnouncementType;
  startDate: string;
  endDate: string;
  targetMemberships: TargetAudience[];
  dismissible: boolean;
  dismissCount: number;
  imageUrl?: string;
  actionUrl?: string;
  priority: number;
  isActive: boolean; // Added isActive field
  createdAt: string;
  updatedAt: string;
}

type FormData = {
  title: string;
  content: string;
  type: AnnouncementType;
  startDate: Date;
  endDate: Date;
  targetMemberships: TargetAudience[];
  dismissible: boolean;
  dismissCount: number;
  imageUrl: string;
  actionUrl: string;
  priority: number;
  isActive: boolean; // Added isActive field
};

const announcementTypeOptions: { value: AnnouncementType; label: string; icon: JSX.Element }[] = [
  { value: 'info', label: 'Information', icon: <Info className="h-4 w-4 text-blue-500" /> },
  { value: 'warning', label: 'Warning', icon: <AlertTriangle className="h-4 w-4 text-amber-500" /> },
  { value: 'critical', label: 'Critical', icon: <BadgeAlert className="h-4 w-4 text-red-500" /> },
  { value: 'promo', label: 'Promotion', icon: <Megaphone className="h-4 w-4 text-purple-500" /> },
  { value: 'update', label: 'Update', icon: <ArrowUpCircle className="h-4 w-4 text-green-500" /> },
];

const targetAudienceOptions: { value: TargetAudience; label: string }[] = [
  { value: 'all', label: 'All Users' },
  { value: 'free', label: 'Free Users' },
  { value: 'premium', label: 'Premium Users' },
  { value: 'vip', label: 'VIP Users' },
];

const announcementSchema = z.object({
  title: z.string().min(1, "Title is required").max(100, "Title is too long"),
  content: z.string().min(1, "Content is required"),
  type: z.enum(['info', 'warning', 'critical', 'promo', 'update']),
  startDate: z.date(),
  endDate: z.date(),
  targetMemberships: z.array(z.enum(['all', 'free', 'premium', 'vip'])).min(1, "At least one target audience is required"),
  dismissible: z.boolean(),
  dismissCount: z.number().min(1).max(10),
  imageUrl: z.string().optional(),
  actionUrl: z.string().optional(),
  priority: z.number().min(1).max(10),
  isActive: z.boolean(), // Added isActive validation
}).refine((data) => {
  return data.startDate <= data.endDate;
}, {
  message: "End date must be after start date",
  path: ["endDate"],
});

export function AnnouncementsTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);
  const [previewAnnouncement, setPreviewAnnouncement] = useState<Announcement | null>(null);
  const [importData, setImportData] = useState("");
  
  const [formData, setFormData] = useState<FormData>({
    title: "",
    content: "",
    type: "info",
    startDate: new Date(),
    endDate: new Date(new Date().setDate(new Date().getDate() + 7)), // Default to 7 days from now
    targetMemberships: ["all"],
    dismissible: true,
    dismissCount: 3,
    imageUrl: "",
    actionUrl: "",
    priority: 1,
    isActive: true, // Default to active
  });

  // Query to get all announcements
  const {
    data: apiResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["/api/announcements"],
    queryFn: () => apiRequest<{success: boolean, data: Announcement[]}>("/api/announcements"),
  });

  // Extract the announcements array from the API response
  const announcements = apiResponse?.data || [];

  // Add new announcement mutation
  const addAnnouncementMutation = useMutation({
    mutationFn: async (data: Omit<FormData, 'startDate' | 'endDate'> & { startDate: string; endDate: string }) => {
      return apiRequest("/api/announcements", {
        method: "POST",
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/announcements"] });
      setIsAddDialogOpen(false);
      resetForm();
      toast({
        title: "Success",
        description: "Announcement added successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add announcement",
        variant: "destructive",
      });
    },
  });

  // Update announcement mutation
  const updateAnnouncementMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Omit<FormData, 'startDate' | 'endDate'> & { startDate: string; endDate: string } }) => {
      return apiRequest(`/api/announcements/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/announcements"] });
      setIsEditDialogOpen(false);
      resetForm();
      toast({
        title: "Success",
        description: "Announcement updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update announcement",
        variant: "destructive",
      });
    },
  });

  // Toggle activation status mutation
  const toggleActivationMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      return apiRequest(`/api/announcements/${id}/toggle-status`, {
        method: "PATCH",
        body: JSON.stringify({ isActive }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/announcements"] });
      toast({
        title: "Success",
        description: "Announcement status updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update announcement status",
        variant: "destructive",
      });
    },
  });

  // Delete announcement mutation
  const deleteAnnouncementMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest(`/api/announcements/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/announcements"] });
      toast({
        title: "Success",
        description: "Announcement deleted successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete announcement",
        variant: "destructive",
      });
    },
  });

  // Import announcements mutation
  const importAnnouncementsMutation = useMutation({
    mutationFn: (importData: string) => {
      try {
        const parsedData = JSON.parse(importData);
        return apiRequest("/api/announcements/import", {
          method: "POST",
          body: JSON.stringify({ announcements: parsedData }),
        });
      } catch (error) {
        throw new Error("Invalid JSON data");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/announcements"] });
      setIsImportDialogOpen(false);
      setImportData("");
      toast({
        title: "Success",
        description: "Announcements imported successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to import announcements",
        variant: "destructive",
      });
    },
  });

  const handleAddSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Validate with Zod
      announcementSchema.parse(formData);
      
      // Convert dates to ISO strings
      const submitData = {
        ...formData,
        startDate: formData.startDate.toISOString(),
        endDate: formData.endDate.toISOString(),
      };
      
      addAnnouncementMutation.mutate(submitData);
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0].message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: "An unexpected error occurred",
          variant: "destructive",
        });
      }
    }
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAnnouncement) return;

    try {
      // Validate with Zod
      announcementSchema.parse(formData);
      
      // Convert dates to ISO strings
      const submitData = {
        ...formData,
        startDate: formData.startDate.toISOString(),
        endDate: formData.endDate.toISOString(),
      };
      
      updateAnnouncementMutation.mutate({
        id: selectedAnnouncement.id,
        data: submitData,
      });
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0].message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: "An unexpected error occurred",
          variant: "destructive",
        });
      }
    }
  };

  const handleToggleActivation = (announcement: Announcement) => {
    toggleActivationMutation.mutate({
      id: announcement.id,
      isActive: !announcement.isActive,
    });
  };

  const handleDelete = (id: string) => {
    deleteAnnouncementMutation.mutate(id);
  };

  const handleImport = () => {
    importAnnouncementsMutation.mutate(importData);
  };

  const handleExport = () => {
    if (!announcements) return;
    
    const jsonData = JSON.stringify(announcements, null, 2);
    const blob = new Blob([jsonData], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `announcements_export_${format(new Date(), "yyyy-MM-dd")}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const openEditDialog = (announcement: Announcement) => {
    setSelectedAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content,
      type: announcement.type,
      startDate: new Date(announcement.startDate),
      endDate: new Date(announcement.endDate),
      targetMemberships: announcement.targetMemberships,
      dismissible: announcement.dismissible,
      dismissCount: announcement.dismissCount,
      imageUrl: announcement.imageUrl || "",
      actionUrl: announcement.actionUrl || "",
      priority: announcement.priority,
      isActive: announcement.isActive,
    });
    setIsEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: "",
      content: "",
      type: "info",
      startDate: new Date(),
      endDate: new Date(new Date().setDate(new Date().getDate() + 7)),
      targetMemberships: ["all"],
      dismissible: true,
      dismissCount: 3,
      imageUrl: "",
      actionUrl: "",
      priority: 1,
      isActive: false, // Changed from true to false - require explicit activation
    });
  };

  const getTypeIcon = (type: AnnouncementType) => {
    const option = announcementTypeOptions.find(opt => opt.value === type);
    return option?.icon || <Info className="h-4 w-4" />;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <RefreshCw className="animate-spin h-8 w-8" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-destructive/10 rounded-lg text-center">
        <h3 className="text-lg font-medium">Error loading announcements</h3>
        <p className="text-muted-foreground">{(error as Error).message}</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() =>
            queryClient.invalidateQueries({ queryKey: ["/api/announcements"] })
          }
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-0">
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => setIsImportDialogOpen(true)}
            className="w-full sm:w-auto"
          >
            Import
          </Button>
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={!announcements || announcements.length === 0}
            className="w-full sm:w-auto"
          >
            Export
          </Button>
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  resetForm();
                  setIsAddDialogOpen(true);
                }}
                className="w-full sm:w-auto"
              >
                <PlusCircle className="h-4 w-4 mr-2" />
                Add New Announcement
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[700px]">
              <DialogHeader>
                <DialogTitle>Create New Announcement</DialogTitle>
                <DialogDescription>
                  Create a new announcement to display to users
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddSubmit}>
                <Tabs defaultValue="basic" className="w-full">
                  <TabsList className="grid grid-cols-4 mb-4">
                    <TabsTrigger value="basic" className="flex items-center gap-2">
                      <FileTextIcon className="h-4 w-4" />
                      <span>Basic Info</span>
                    </TabsTrigger>
                    <TabsTrigger value="schedule" className="flex items-center gap-2">
                      <CalendarIcon className="h-4 w-4" />
                      <span>Schedule</span>
                    </TabsTrigger>
                    <TabsTrigger value="display" className="flex items-center gap-2">
                      <LayoutIcon className="h-4 w-4" />
                      <span>Display</span>
                    </TabsTrigger>
                    <TabsTrigger value="visibility" className="flex items-center gap-2">
                      <SettingsIcon className="h-4 w-4" />
                      <span>Visibility</span>
                    </TabsTrigger>
                  </TabsList>

                  {/* Basic Info Tab */}
                  <TabsContent value="basic">
                    <Card>
                      <CardContent className="pt-6 space-y-4">
                        <div className="grid gap-4">
                          <div className="grid gap-2">
                            <Label htmlFor="title">Title</Label>
                            <Input
                              id="title"
                              value={formData.title}
                              onChange={(e) =>
                                setFormData({ ...formData, title: e.target.value })
                              }
                              placeholder="Enter announcement title"
                              required
                            />
                          </div>
                          
                          <div className="grid gap-2">
                            <Label htmlFor="content">Content</Label>
                            <Textarea
                              id="content"
                              value={formData.content}
                              onChange={(e) =>
                                setFormData({ ...formData, content: e.target.value })
                              }
                              placeholder="Enter announcement content"
                              required
                              className="min-h-[150px]"
                            />
                          </div>
                          
                          <div className="grid gap-2">
                            <Label htmlFor="type">Announcement Type</Label>
                            <Select
                              value={formData.type}
                              onValueChange={(value: AnnouncementType) =>
                                setFormData({ ...formData, type: value })
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select announcement type" />
                              </SelectTrigger>
                              <SelectContent>
                                {announcementTypeOptions.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    <div className="flex items-center">
                                      {option.icon}
                                      <span className="ml-2">{option.label}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Schedule & Targeting Tab */}
                  <TabsContent value="schedule">
                    <Card>
                      <CardContent className="pt-6 space-y-4">
                        <div className="grid gap-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="grid gap-2">
                              <Label>Start Date</Label>
                              <DatePicker
                                date={formData.startDate}
                                setDate={(date) => {
                                  if (date) {
                                    setFormData({ ...formData, startDate: date });
                                  }
                                }}
                              />
                            </div>
                            
                            <div className="grid gap-2">
                              <Label>End Date</Label>
                              <DatePicker
                                date={formData.endDate}
                                setDate={(date) => {
                                  if (date) {
                                    setFormData({ ...formData, endDate: date });
                                  }
                                }}
                              />
                            </div>
                          </div>
                          
                          <div className="grid gap-2">
                            <Label>Target Audience</Label>
                            <div className="flex flex-col space-y-2 border rounded-md p-3">
                              {targetAudienceOptions.map((option) => (
                                <div key={option.value} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`target-${option.value}`}
                                    checked={formData.targetMemberships.includes(option.value)}
                                    onCheckedChange={(checked) => {
                                      if (checked) {
                                        setFormData({
                                          ...formData,
                                          targetMemberships: [...formData.targetMemberships, option.value],
                                        });
                                      } else {
                                        setFormData({
                                          ...formData,
                                          targetMemberships: formData.targetMemberships.filter(
                                            (item) => item !== option.value
                                          ),
                                        });
                                      }
                                    }}
                                  />
                                  <Label htmlFor={`target-${option.value}`} className="cursor-pointer">
                                    {option.label}
                                  </Label>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Display Options Tab */}
                  <TabsContent value="display">
                    <Card>
                      <CardContent className="pt-6 space-y-4">
                        <div className="grid gap-4">
                          <div className="grid gap-2">
                            

                            <ImageUploadField
                              id="imageUrl"
                              label="Image URL"
                              value={formData.imageUrl}
                              onChange={(value) =>
                                setFormData({ ...formData, imageUrl: value })
                              }
                              placeholder="Enter image URL or upload an image"
                              uploadEndpoint="/api/announcements/upload-image"
                            />
                          </div>
                          
                          <div className="grid gap-2">
                            <Label htmlFor="actionUrl">Action URL (Optional)</Label>
                            <Input
                              id="actionUrl"
                              value={formData.actionUrl}
                              onChange={(e) =>
                                setFormData({ ...formData, actionUrl: e.target.value })
                              }
                              placeholder="Enter URL for action button"
                            />
                          </div>
                          
                          <div className="grid gap-2">
                            <Label htmlFor="priority">Priority (1-10)</Label>
                            <Select
                              value={formData.priority.toString()}
                              onValueChange={(value) => 
                                setFormData({ ...formData, priority: parseInt(value) })
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select priority level" />
                              </SelectTrigger>
                              <SelectContent>
                                {[...Array(10)].map((_, i) => (
                                  <SelectItem key={i+1} value={(i+1).toString()}>
                                    {i+1} - {i < 3 ? 'Low' : i < 7 ? 'Medium' : 'High'} Priority
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground mt-1">
                              Higher priority announcements will be shown first
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Visibility Settings Tab */}
                  <TabsContent value="visibility">
                    <Card>
                      <CardContent className="pt-6 space-y-6">
                        <div className="flex flex-col space-y-4">
                          <div className="flex items-center space-x-4 pb-4 border-b">
                            <Switch
                              id="isActive"
                              checked={formData.isActive}
                              onCheckedChange={(checked) =>
                                setFormData({ ...formData, isActive: checked })
                              }
                            />
                            <div>
                              <Label htmlFor="isActive" className="font-medium text-base">
                                {formData.isActive ? "Announcement is active" : "Announcement is inactive"}
                              </Label>
                              <p className="text-sm text-muted-foreground mt-1">
                                {formData.isActive 
                                  ? "The announcement will be visible to users right away if the start date has passed." 
                                  : "The announcement will be saved but won't be shown to users until you activate it."}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-4 pb-4 border-b">
                            <Switch
                              id="dismissible"
                              checked={formData.dismissible}
                              onCheckedChange={(checked) =>
                                setFormData({ ...formData, dismissible: checked })
                              }
                            />
                            <div>
                              <Label htmlFor="dismissible" className="font-medium text-base">
                                Allow users to dismiss this announcement
                              </Label>
                              <p className="text-sm text-muted-foreground mt-1">
                                When enabled, users can dismiss this announcement temporarily or permanently
                              </p>
                            </div>
                          </div>
                          
                          {formData.dismissible && (
                            <div className="pl-10 pt-2">
                              <Label htmlFor="dismissCount" className="mb-2 block">
                                Dismiss count before permanent dismiss
                              </Label>
                              <div className="flex items-center gap-4">
                                <Select
                                  value={formData.dismissCount.toString()}
                                  onValueChange={(value) => 
                                    setFormData({ ...formData, dismissCount: parseInt(value) })
                                  }
                                  disabled={!formData.dismissible}
                                >
                                  <SelectTrigger className="w-[180px]">
                                    <SelectValue placeholder="Select dismiss count" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {[...Array(10)].map((_, i) => (
                                      <SelectItem key={i+1} value={(i+1).toString()}>
                                        {i+1} {i === 0 ? 'time' : 'times'}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <p className="text-sm text-muted-foreground">
                                  Number of dismissals before permanent hiding
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
                
                <DialogFooter className="mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsAddDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={addAnnouncementMutation.isPending}
                  >
                    {addAnnouncementMutation.isPending ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>Create Announcement</>
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border">
        <Table>
          <TableCaption>Announcement Management</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[180px]">Title</TableHead>
              <TableHead className="min-w-[120px]">Type</TableHead>
              <TableHead className="min-w-[120px]">Start Date</TableHead>
              <TableHead className="min-w-[120px]">End Date</TableHead>
              <TableHead className="min-w-[120px]">Audience</TableHead>
              <TableHead className="min-w-[80px]">Priority</TableHead>
              <TableHead className="min-w-[100px]">Status</TableHead>
              <TableHead className="text-right min-w-[160px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!announcements || announcements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center">
                  No announcements found. Create your first announcement to get started.
                </TableCell>
              </TableRow>
            ) : (
              announcements.map((announcement) => (
                <TableRow key={announcement.id} className={!announcement.isActive ? "opacity-60" : ""}>
                  <TableCell className="font-medium">{announcement.title}</TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      {getTypeIcon(announcement.type)}
                      <span className="ml-2 capitalize">{announcement.type}</span>
                    </div>
                  </TableCell>
                  <TableCell>{format(new Date(announcement.startDate), "MMM d, yyyy")}</TableCell>
                  <TableCell>{format(new Date(announcement.endDate), "MMM d, yyyy")}</TableCell>
                  <TableCell>
                    {announcement.targetMemberships.includes("all") ? (
                      <span>All Users</span>
                    ) : (
                      <span>
                        {announcement.targetMemberships
                          .map((audience) => 
                            targetAudienceOptions.find((opt) => opt.value === audience)?.label
                          )
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>{announcement.priority}</TableCell>
                  <TableCell>
                    <Badge 
                      variant={announcement.isActive ? "outline" : "secondary"}
                      className={announcement.isActive ? "bg-green-100 hover:bg-green-200 text-green-800" : "bg-gray-100 hover:bg-gray-200 text-gray-800"}
                    >
                      {announcement.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className={announcement.isActive ? "bg-red-50 hover:bg-red-100 text-red-600" : "bg-green-50 hover:bg-green-100 text-green-600"}
                        onClick={() => handleToggleActivation(announcement)}
                        title={announcement.isActive ? "Deactivate announcement" : "Activate announcement"}
                      >
                        {announcement.isActive ? (
                          <PowerOff className="h-4 w-4" />
                        ) : (
                          <Power className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => openEditDialog(announcement)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setPreviewAnnouncement(announcement);
                          setIsPreviewDialogOpen(true);
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="destructive" size="sm">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Announcement</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{announcement.title}"? This
                              action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(announcement.id)}
                              className="bg-destructive text-destructive-foreground"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>Edit Announcement</DialogTitle>
            <DialogDescription>
              Update the announcement details
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit}>
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid grid-cols-4 mb-4">
                <TabsTrigger value="basic" className="flex items-center gap-2">
                  <FileTextIcon className="h-4 w-4" />
                  <span>Basic Info</span>
                </TabsTrigger>
                <TabsTrigger value="schedule" className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  <span>Schedule</span>
                </TabsTrigger>
                <TabsTrigger value="display" className="flex items-center gap-2">
                  <LayoutIcon className="h-4 w-4" />
                  <span>Display</span>
                </TabsTrigger>
                <TabsTrigger value="visibility" className="flex items-center gap-2">
                  <SettingsIcon className="h-4 w-4" />
                  <span>Visibility</span>
                </TabsTrigger>
              </TabsList>

              {/* Basic Info Tab */}
              <TabsContent value="basic">
                <Card>
                  <CardContent className="pt-6 space-y-4">
                    <div className="grid gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor="edit-title">Title</Label>
                        <Input
                          id="edit-title"
                          value={formData.title}
                          onChange={(e) =>
                            setFormData({ ...formData, title: e.target.value })
                          }
                          placeholder="Enter announcement title"
                          required
                        />
                      </div>
                      
                      <div className="grid gap-2">
                        <Label htmlFor="edit-content">Content</Label>
                        <Textarea
                          id="edit-content"
                          value={formData.content}
                          onChange={(e) =>
                            setFormData({ ...formData, content: e.target.value })
                          }
                          placeholder="Enter announcement content"
                          required
                          className="min-h-[150px]"
                        />
                      </div>
                      
                      <div className="grid gap-2">
                        <Label htmlFor="edit-type">Announcement Type</Label>
                        <Select
                          value={formData.type}
                          onValueChange={(value: AnnouncementType) =>
                            setFormData({ ...formData, type: value })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select announcement type" />
                          </SelectTrigger>
                          <SelectContent>
                            {announcementTypeOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                <div className="flex items-center">
                                  {option.icon}
                                  <span className="ml-2">{option.label}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Schedule & Targeting Tab */}
              <TabsContent value="schedule">
                <Card>
                  <CardContent className="pt-6 space-y-4">
                    <div className="grid gap-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="grid gap-2">
                          <Label>Start Date</Label>
                          <DatePicker
                            date={formData.startDate}
                            setDate={(date) => {
                              if (date) {
                                setFormData({ ...formData, startDate: date });
                              }
                            }}
                          />
                        </div>
                        
                        <div className="grid gap-2">
                          <Label>End Date</Label>
                          <DatePicker
                            date={formData.endDate}
                            setDate={(date) => {
                              if (date) {
                                setFormData({ ...formData, endDate: date });
                              }
                            }}
                          />
                        </div>
                      </div>
                      
                      <div className="grid gap-2">
                        <Label>Target Audience</Label>
                        <div className="flex flex-col space-y-2 border rounded-md p-3">
                          {targetAudienceOptions.map((option) => (
                            <div key={option.value} className="flex items-center space-x-2">
                              <Checkbox
                                id={`edit-target-${option.value}`}
                                checked={formData.targetMemberships.includes(option.value)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setFormData({
                                      ...formData,
                                      targetMemberships: [...formData.targetMemberships, option.value],
                                    });
                                  } else {
                                    setFormData({
                                      ...formData,
                                      targetMemberships: formData.targetMemberships.filter(
                                        (item) => item !== option.value
                                      ),
                                    });
                                  }
                                }}
                              />
                              <Label htmlFor={`edit-target-${option.value}`} className="cursor-pointer">
                                {option.label}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Display Options Tab */}
              <TabsContent value="display">
                <Card>
                  <CardContent className="pt-6 space-y-4">
                    <div className="grid gap-4">
                      <div className="grid gap-2">
                        

                        <ImageUploadField
                              id="imageUrl"
                              label="Image URL"
                              value={formData.imageUrl}
                              onChange={(value) =>
                                setFormData({ ...formData, imageUrl: value })
                              }
                              placeholder="Enter image URL or upload an image"
                              uploadEndpoint="/api/announcements/upload-image"
                            />

                            
                        {formData.imageUrl && (
                          <div className="mt-2">
                            <Label>Current Image:</Label>
                            <div className="relative mt-1 h-32 w-full rounded-md border border-dashed">
                              <img
                                src={formData.imageUrl}
                                alt={formData.title}
                                className="h-full w-auto max-w-full object-contain mx-auto"
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src = "https://placehold.co/400x200/cccccc/969696?text=Invalid+Image+URL";
                                  toast({
                                    title: "Image Error",
                                    description: "Could not load the image. The URL may be invalid.",
                                    variant: "destructive",
                                  });
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="grid gap-2">
                        <Label htmlFor="edit-actionUrl">Action URL (Optional)</Label>
                        <Input
                          id="edit-actionUrl"
                          value={formData.actionUrl}
                          onChange={(e) =>
                            setFormData({ ...formData, actionUrl: e.target.value })
                          }
                          placeholder="Enter URL for action button"
                        />
                      </div>
                      
                      <div className="grid gap-2">
                        <Label htmlFor="priority">Priority (1-10)</Label>
                        <Select
                          value={formData.priority.toString()}
                          onValueChange={(value) => 
                            setFormData({ ...formData, priority: parseInt(value) })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select priority level" />
                          </SelectTrigger>
                          <SelectContent>
                            {[...Array(10)].map((_, i) => (
                              <SelectItem key={i+1} value={(i+1).toString()}>
                                {i+1} - {i < 3 ? 'Low' : i < 7 ? 'Medium' : 'High'} Priority
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">
                          Higher priority announcements will be shown first
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Visibility Settings Tab */}
              <TabsContent value="visibility">
                <Card>
                  <CardContent className="pt-6 space-y-6">
                    <div className="flex flex-col space-y-4">
                      <div className="flex items-center space-x-4 pb-4 border-b">
                        <Switch
                          id="edit-isActive"
                          checked={formData.isActive}
                          onCheckedChange={(checked) =>
                            setFormData({ ...formData, isActive: checked })
                          }
                        />
                        <div>
                          <Label htmlFor="edit-isActive" className="font-medium text-base">
                            {formData.isActive ? "Announcement is active" : "Announcement is inactive"}
                          </Label>
                          <p className="text-sm text-muted-foreground mt-1">
                            {formData.isActive 
                              ? "The announcement is visible to users if the current date is within the announcement period." 
                              : "The announcement is not visible to users, even if the current date is within the announcement period."}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 pb-4 border-b">
                        <Switch
                          id="edit-dismissible"
                          checked={formData.dismissible}
                          onCheckedChange={(checked) =>
                            setFormData({ ...formData, dismissible: checked })
                          }
                        />
                        <div>
                          <Label htmlFor="edit-dismissible" className="font-medium text-base">
                            Allow users to dismiss this announcement
                          </Label>
                          <p className="text-sm text-muted-foreground mt-1">
                            When enabled, users can dismiss this announcement temporarily or permanently
                          </p>
                        </div>
                      </div>
                      
                      {formData.dismissible && (
                        <div className="pl-10 pt-2">
                          <Label htmlFor="edit-dismissCount" className="mb-2 block">
                            Dismiss count before permanent dismiss
                          </Label>
                          <div className="flex items-center gap-4">
                            <Select
                              value={formData.dismissCount.toString()}
                              onValueChange={(value) => 
                                setFormData({ ...formData, dismissCount: parseInt(value) })
                              }
                              disabled={!formData.dismissible}
                            >
                              <SelectTrigger className="w-[180px]">
                                <SelectValue placeholder="Select dismiss count" />
                              </SelectTrigger>
                              <SelectContent>
                                {[...Array(10)].map((_, i) => (
                                  <SelectItem key={i+1} value={(i+1).toString()}>
                                    {i+1} {i === 0 ? 'time' : 'times'}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <p className="text-sm text-muted-foreground">
                              Number of dismissals before permanent hiding
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
            
            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={updateAnnouncementMutation.isPending}
              >
                {updateAnnouncementMutation.isPending ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>Save Changes</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Import Announcements</DialogTitle>
            <DialogDescription>
              Paste JSON data to import announcements
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              value={importData}
              onChange={(e) => setImportData(e.target.value)}
              placeholder="Paste JSON data here..."
              className="min-h-[200px]"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsImportDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleImport}
              disabled={importAnnouncementsMutation.isPending || !importData.trim()}
            >
              {importAnnouncementsMutation.isPending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>Import</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={isPreviewDialogOpen} onOpenChange={setIsPreviewDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Preview Announcement</DialogTitle>
            <DialogDescription>
              Preview the announcement as it will appear to users
            </DialogDescription>
          </DialogHeader>
          {previewAnnouncement && (
            <div className="py-4">
              <h3 className="text-xl font-bold">{previewAnnouncement.title}</h3>
              <p className="text-muted-foreground mb-4">{previewAnnouncement.content}</p>
              {previewAnnouncement.imageUrl && (
                <div className="mb-4">
                  <img
                    src={previewAnnouncement.imageUrl}
                    alt={previewAnnouncement.title}
                    className="w-full h-auto rounded-md"
                  />
                </div>
              )}
              {previewAnnouncement.actionUrl && (
                <a href={previewAnnouncement.actionUrl} target="_blank" rel="noopener noreferrer">
                  <Button type="button">
                    Learn More
                  </Button>
                </a>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsPreviewDialogOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}